// Pricing FAQ for the dedicated /pricing page.
//
// Every answer here is sourced from copy that already ships in the product
// (the AgencyPricingPlans bullets in agencyTiers.ts + the /producthunt
// highlights). We do not invent policies. If a tier doesn't actually
// deliver a feature today, it doesn't get mentioned here.

import { type ReactNode } from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';

type FaqEntry = { value: string; q: string; a: ReactNode };

const FAQS: FaqEntry[] = [
  {
    value: 'free',
    q: 'How does the 7-day free trial work?',
    a: 'Click "Try free for 7 days" on the Starter card. No credit card required. Sign in with Google and you get 7 days of full Starter-tier access: 5,000 credits, all 13 audit tools unlocked, fully white-labelled (no Fly logo on reports or embeds). After day 7, your tools pause and your embed shows a "trial expired" badge until you pick a paid plan. Your captured leads and past reports remain accessible the whole time.',
  },
  {
    value: 'credits',
    q: 'How do credits work?',
    a: 'Each paid plan includes a monthly credit allowance (5,000 on Starter, 19,000 on Growth, 54,500 on Scale) that you spend running tools and capturing leads. Need more before your next cycle? Top-up packs (+1,000 / +2,500 / +7,500 / +20,000 / +50,000 credits) are available on every paid plan and never expire.',
  },
  {
    value: 'currency',
    q: 'What currency will I be billed in?',
    a: 'Prices are shown in USD. Indian agencies are billed in INR at a locked rate of ₹83 to $1, with a GST-compliant tax invoice.',
  },
  {
    value: 'cancel',
    q: 'Can I cancel anytime?',
    a: 'Yes. You can cancel anytime and you only ever pay for the credits you consume.',
  },
  {
    value: 'white-label',
    q: 'What does white-label include?',
    a: 'It depends on your plan. Starter adds your logo plus one brand colour. Growth adds white-label PDF reports and lets you point a custom domain at your tools with your own brand colours. Scale inherits everything in Growth.',
  },
  {
    value: 'leads',
    q: 'How are leads delivered to me?',
    a: 'Every plan delivers leads by email and in your dashboard the moment they\'re captured. The dashboard exports + integrations roadmap (CSV, webhooks, CRM sync) is on the way; we\'ll surface them on the pricing page the day they ship.',
  },
];

export function PricingFaq() {
  return (
    <section className="relative bg-gradient-to-b from-gray-950 to-[#0d0d14] py-24 lg:py-28">
      <div className="container mx-auto px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center mb-12">
          <div className="text-xs uppercase tracking-[0.2em] text-fuchsia-400 font-semibold mb-4">
            FAQ
          </div>
          <h2
            className="text-white font-bold tracking-tight"
            style={{ fontSize: 'clamp(1.8rem, 3.5vw, 2.8rem)', lineHeight: 1.1 }}
          >
            Pricing questions, answered.
          </h2>
        </div>

        <Accordion type="single" collapsible className="max-w-3xl mx-auto space-y-3">
          {FAQS.map(({ value, q, a }) => (
            <AccordionItem
              key={value}
              value={value}
              className="rounded-xl border border-white/10 bg-white/[0.03] px-5 transition-colors hover:border-white/20 data-[state=open]:border-fuchsia-500/30 data-[state=open]:bg-white/[0.05]"
            >
              <AccordionTrigger className="py-5 text-left text-base font-semibold text-white hover:no-underline">
                {q}
              </AccordionTrigger>
              <AccordionContent className="pb-5 text-sm leading-relaxed text-white/70">
                {a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}
